/**
 * PopupTargetsPage - Stateless targets viewer popup
 *
 * Receives all data from the main window via BroadcastChannel.
 * Does NOT create its own WebSocket connections.
 *
 * Uses the "stale while revalidate" pattern - shows existing data
 * with a refresh indicator while new data is being fetched.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  Button,
  Input,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandGroup,
  CommandItem,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { ChevronDown, X } from 'lucide-react';
import { PiFileCsv, PiBoat } from 'react-icons/pi';
import { TargetFromTargetList } from '@ais-ui/websockets';
import {
  PopupType,
  getPopupIdFromUrl,
  getPopupParams,
  closePopup,
} from '@ais-ui/popups';
import {
  PopupActions,
  MessageType,
  useMainWindowMessages,
  PopupMessage,
  DataType,
} from './PopupEvents';
import { usePopupLifecycle } from './usePopupLifecycle';
import { PopupConnectionStatus } from './shared/PopupConnectionStatus';
import { usePopupPerformanceTracking } from './usePopupPerformanceTracking';
import { usePopupDataState } from './usePopupDataState';
import { PopupRefreshIndicator } from './PopupRefreshIndicator';
import { PopupDistributionChart, getDensityClasses } from './shared';
import { TargetsTableSection } from './TargetsTableSection';
import { TargetsBoundsCard } from './TargetsBoundsCard';

/**
 * Data structure sent from main window
 */
export interface PopupTargetsData {
  targets: TargetFromTargetList[];
  total: number;
  retrievalTime: string;
  bounds: {
    minLat: number;
    maxLat: number;
    minLon: number;
    maxLon: number;
  };
  settings?: {
    tableDensity: 'normal' | 'compact';
  };
  /** Unique key for this data snapshot - used to prevent unnecessary re-renders */
  dataKey?: string;
}

const TARGETS_CHART_OPTIONS = [
  { value: 'country', label: 'Country' },
  { value: 'shipType', label: 'Ship Type' },
  { value: 'type', label: 'AIS Type' },
];

export const PopupTargetsPage: React.FC = () => {
  const popupId = getPopupIdFromUrl();
  const params = getPopupParams();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { messages } = useLocalization();

  // Use the popup data state hook for "stale while revalidate" pattern
  const {
    data,
    isInitialLoading,
    isRefreshing,
    error,
    setData,
    setRefreshing,
    setError,
  } = usePopupDataState<PopupTargetsData>();

  const dataReceivedRef = useRef(false);
  const dataKeyRef = useRef<string | undefined>(undefined);

  // Memoize metadata to prevent performance tracking from re-creating callbacks
  const trackingMetadata = useMemo(
    () => ({
      minLat: params.minLat || 'unknown',
      maxLat: params.maxLat || 'unknown',
    }),
    [params.minLat, params.maxLat],
  );

  // Performance tracking
  const { trackDataRequest, trackDataReceived, trackError, trackTimeout } =
    usePopupPerformanceTracking({
      popupId,
      popupType: PopupType.Targets,
      metadata: trackingMetadata,
    });

  // Table density from settings (default to compact)
  const tableDensity = data?.settings?.tableDensity || 'compact';
  const densityClasses = getDensityClasses(tableDensity);

  // Sorting state (client-side only)
  const [orderBy, setOrderBy] = useState('name');
  const [orderAscending, setOrderAscending] = useState(true);

  // Pagination state (client-side)
  const [page, setPage] = useState(1);
  const perPage = 100;

  // Chart state
  const [chartGroupBy, setChartGroupBy] = useState('country');
  const [activeTab, setActiveTab] = useState('chart');

  // Shiplist state
  const [shiplistName, setShiplistName] = useState('');
  const [isShiplistPopoverOpen, setIsShiplistPopoverOpen] = useState(false);

  // Request initial data from main window
  useEffect(() => {
    if (!popupId) {
      setError('No popup ID found');
      trackError('No popup ID found');
      return;
    }

    dataReceivedRef.current = false;
    trackDataRequest();

    PopupActions.requestInit({
      popupId,
      popupType: PopupType.Targets,
      params,
    });

    // 30 seconds to allow for large target datasets when zoomed out
    const timeout = setTimeout(() => {
      if (!dataReceivedRef.current) {
        setError('Timeout waiting for data from main window.');
        trackTimeout();
      }
    }, 30000);

    return () => clearTimeout(timeout);
  }, [popupId, trackDataRequest, trackError, trackTimeout]);

  // Listen for messages from main window
  useMainWindowMessages(
    (msg: PopupMessage) => {
      if (
        msg.type === MessageType.POPUP_INIT_RESPONSE &&
        msg.popupId === popupId
      ) {
        dataReceivedRef.current = true;
        const payload = msg.payload as PopupTargetsData;

        // Only update if dataKey is different to prevent unnecessary re-renders (flickering)
        if (payload.dataKey && payload.dataKey === dataKeyRef.current) {
          setRefreshing(false);
          return;
        }

        dataKeyRef.current = payload.dataKey;
        setData(payload);
        trackDataReceived(payload.targets?.length);
        document.title = 'Targets on Map';
      }

      if (msg.type === MessageType.DATA_UPDATE) {
        const payload = msg.payload as {
          dataType: string;
          data: PopupTargetsData | null;
          isLoading?: boolean;
        };
        if (payload.dataType === DataType.TARGETS) {
          if (payload.isLoading) {
            // Data fetch started - show refresh indicator
            setRefreshing(true);
          } else if (payload.data) {
            // Only update if dataKey is different
            if (
              payload.data.dataKey &&
              payload.data.dataKey === dataKeyRef.current
            ) {
              setRefreshing(false);
              return;
            }
            dataKeyRef.current = payload.data.dataKey;
            setData(payload.data);
          }
        }
      }

      if (msg.type === MessageType.SESSION_ENDED) {
        setError('Session ended. Please close this window.');
        trackError('Session ended');
      }
    },
    popupId ?? undefined,
    [popupId, setRefreshing, trackDataReceived, trackError],
  );

  // Handle popup lifecycle (close notification, heartbeat)
  const connectionState = usePopupLifecycle(popupId, PopupType.Targets);

  // Sort and paginate targets client-side
  const sortedTargets = useMemo(() => {
    if (!data?.targets) return [];

    return [...data.targets].sort((a, b) => {
      const aVal = a[orderBy as keyof TargetFromTargetList] ?? '';
      const bVal = b[orderBy as keyof TargetFromTargetList] ?? '';

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return orderAscending
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      const aNum = Number(aVal) || 0;
      const bNum = Number(bVal) || 0;
      return orderAscending ? aNum - bNum : bNum - aNum;
    });
  }, [data?.targets, orderBy, orderAscending]);

  const paginatedTargets = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedTargets.slice(start, start + perPage);
  }, [sortedTargets, page, perPage]);

  const totalPages = Math.ceil((data?.total || 0) / perPage);

  // Chart data
  const chartData = useMemo(() => {
    if (!data?.targets?.length) return [];

    const grouped: Record<string, number> = {};
    data.targets.forEach((target) => {
      const key =
        (target[chartGroupBy as keyof TargetFromTargetList] as string) ||
        'Unknown';
      grouped[key] = (grouped[key] || 0) + 1;
    });

    return Object.entries(grouped)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);
  }, [data?.targets, chartGroupBy]);

  const handleSort = useCallback(
    (column: string) => {
      if (orderBy === column) {
        setOrderAscending(!orderAscending);
      } else {
        setOrderBy(column);
        setOrderAscending(true);
      }
      setPage(1);
    },
    [orderBy, orderAscending],
  );

  const handleViewTarget = useCallback((target: TargetFromTargetList) => {
    PopupActions.viewTarget({
      name: target.name !== target.mmsi ? target.name : '',
      mmsi: target.mmsi,
      imo: target.imo,
      callSign: target.callSign,
    });
  }, []);

  const exportToCSV = useCallback(() => {
    if (!data?.targets) return;

    const headers = [
      'name',
      'mmsi',
      'imo',
      'callSign',
      'country',
      'type',
      'shipType',
      'lat',
      'lon',
      'sog',
      'cog',
    ];
    const formatValue = (v: unknown) => {
      if (v == null) return '';
      const s = String(v);
      return s.includes(',') || s.includes('"')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };

    const csv = [
      headers.join(','),
      ...data.targets.map((t) =>
        headers
          .map((h) => formatValue(t[h as keyof TargetFromTargetList]))
          .join(','),
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `targets_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [data?.targets]);

  const handleCreateShiplist = useCallback(() => {
    if (!data?.targets || !shiplistName.trim()) return;

    PopupActions.createShiplist({
      name: shiplistName,
      targets: data.targets.map((t) => ({
        name: t.name,
        mmsi: t.mmsi,
        imo: t.imo,
      })),
    });

    setShiplistName('');
    setIsShiplistPopoverOpen(false);
  }, [data?.targets, shiplistName]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <div className="text-destructive text-lg font-medium mb-4">
          {messages.popupGenericError}
        </div>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={closePopup}>{messages.popupGenericCloseWindow}</Button>
      </div>
    );
  }

  if (isInitialLoading || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <LoadingSpinner className="h-8 w-8" />
        <p className="mt-4 text-muted-foreground">
          {messages.popupTargetsLoading}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gradient-to-b from-primary/5 via-background to-background relative">
      {/* Connection status overlay */}
      <PopupConnectionStatus connectionState={connectionState} />

      {/* Refresh indicator overlay - doesn't shift layout */}
      <PopupRefreshIndicator
        isRefreshing={isRefreshing}
        message={messages.popupGenericUpdatingData}
      />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">
            {messages.popupTargetsTitle}
          </h1>
          <p className="text-sm text-muted-foreground">
            {data.total}{' '}
            {messages.popupTargetsVesselCount?.replace('{count}', '') ||
              'vessels'}
            {data.retrievalTime && (
              <span className="ml-2">
                •{' '}
                {messages.popupTargetsUpdated?.replace(
                  '{time}',
                  formatDateTimeWithTimeZone(new Date(data.retrievalTime)),
                )}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="default">
                {messages.popupTargetsActions}{' '}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56">
              <Command>
                <CommandGroup>
                  <CommandItem onSelect={exportToCSV}>
                    <PiFileCsv className="mr-2 h-4 w-4" />{' '}
                    {messages.popupGenericExportCsv}
                  </CommandItem>
                  <Popover
                    open={isShiplistPopoverOpen}
                    onOpenChange={setIsShiplistPopoverOpen}
                  >
                    <PopoverTrigger asChild>
                      <CommandItem
                        onSelect={() => setIsShiplistPopoverOpen(true)}
                      >
                        <PiBoat className="mr-2 h-4 w-4" />{' '}
                        {messages.targetsOnMapCreateShiplist ||
                          'Create Ship List'}
                      </CommandItem>
                    </PopoverTrigger>
                    <PopoverContent side="right">
                      <Input
                        aria-label={messages.shiplistsSaveShiplistNameLabel}
                        placeholder={
                          messages.shiplistsSaveShiplistNamePlaceholder
                        }
                        value={shiplistName}
                        onChange={(e) => setShiplistName(e.target.value)}
                      />
                      <div className="w-full flex justify-end">
                        <Button
                          onClick={handleCreateShiplist}
                          className="mt-2"
                          disabled={!shiplistName.trim()}
                        >
                          {messages.genericCreate || 'Create'}
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
          <Button variant="ghost" size="icon" onClick={closePopup}>
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
          <TabsList>
            <TabsTrigger value="chart">
              {messages.popupTargetsChartTab}
            </TabsTrigger>
            <TabsTrigger value="details">
              {messages.popupTargetsDetailsTab}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="chart" className="mt-4">
            <PopupDistributionChart
              data={chartData}
              groupBy={chartGroupBy}
              onGroupByChange={setChartGroupBy}
              options={TARGETS_CHART_OPTIONS.map((opt) => ({
                value: opt.value,
                label:
                  messages[`popupTargetsChart${opt.label.replace(' ', '')}`] ||
                  opt.label,
              }))}
              title={messages.popupTargetsDistributionBy}
              noDataText={messages.popupTargetsChartNoData}
            />
          </TabsContent>

          <TabsContent value="details" className="mt-4">
            <TargetsBoundsCard
              bounds={data.bounds}
              total={data.total}
              messages={messages}
            />
          </TabsContent>
        </Tabs>

        {/* Table */}
        <TargetsTableSection
          targets={paginatedTargets}
          totalCount={data.total}
          page={page}
          totalPages={totalPages}
          perPage={perPage}
          orderBy={orderBy}
          orderAscending={orderAscending}
          onSort={handleSort}
          onPageChange={setPage}
          onViewTarget={handleViewTarget}
          densityClasses={densityClasses}
          messages={messages}
        />
      </div>
    </div>
  );
};

export default PopupTargetsPage;
